import { Range, getTrackBackground } from 'react-range';
import cx from 'classnames';
import { Dispatch, SetStateAction, useState } from 'react';
import classNames from 'classnames';

const isNumeric = (n: any) => !isNaN(parseFloat(n)) && isFinite(n);

const MAX_SAVINGS_LIMIT = 100000;
const MAX_CONTRIBUTIONS_LIMIT = 10000;

interface StateHandlersType {
  setCurrentSavings: Dispatch<SetStateAction<string>>;
  setRateOfReturn: Dispatch<SetStateAction<number | 'custom'>>;
  setCustomRate: Dispatch<SetStateAction<string>>;
  setExtraContribution: Dispatch<SetStateAction<string>>;
  setContributionFrequency: Dispatch<SetStateAction<number>>;
  setSavingsAgeRange: Dispatch<SetStateAction<[number, number]>>;
}

interface SavingsCalculatorFormData {
  currentSavings: string;
  rateOfReturn: number | 'custom';
  customRate: string;
  extraContribution: string;
  contributionFrequency: number;
  savingsAgeRange: [number, number];
  estimatedSavings: number;
}

export interface SavingsCalculatorFormProps {
  stateHandlers: StateHandlersType;
  formData: SavingsCalculatorFormData;
  formSectionHeader?: string;
  currentSavingsHeader?: string;
  contributionsHeader?: string;
  frequencyHeader?: string;
  frequencyOptions?: (
    | 'Bi-weekly'
    | 'Daily'
    | 'Monthly'
    | 'Quarterly'
    | 'Weekly'
    | 'Yearly'
  )[];
  savingsRangeHeader?: string;
  savingsRateHeader?: string;
  savingsRateBody?: string;
  totalSavingsHeader?: string;
}

export const SavingsForm = ({
  stateHandlers,
  formData,
  formSectionHeader,
  currentSavingsHeader,
  contributionsHeader,
  frequencyHeader,
  frequencyOptions,
  savingsRangeHeader,
  savingsRateHeader,
  savingsRateBody,
  totalSavingsHeader
}: SavingsCalculatorFormProps) => {
  const {
    currentSavings,
    rateOfReturn,
    extraContribution,
    contributionFrequency,
    savingsAgeRange
  } = formData;

  const {
    setCurrentSavings,
    setRateOfReturn,
    setExtraContribution,
    setContributionFrequency,
    setSavingsAgeRange
  } = stateHandlers;

  const handleMonetaryValueChange = (
    value: string,
    func: Dispatch<SetStateAction<string>>
  ) => {
    if (isNumeric(value) || value === '') {
      func(value === '' ? '0' : value);
    }
  };

  // Add touched state for both inputs
  const [currentSavingsTouched, setCurrentSavingsTouched] = useState(false);
  const [extraContributionTouched, setExtraContributionTouched] =
    useState(false);

  return (
    <div className="flex w-full flex-col gap-6 tablet:rounded-card-shape tablet:bg-white tablet:p-6 desktop:w-1/3">
      {/* Form Fields */}
      {formSectionHeader && (
        <h2 className="typography-display-4 hidden font-normal tablet:block">
          {formSectionHeader}
        </h2>
      )}
      {/* Initial Deposit */}
      <div className="flex flex-col gap-2">
        {currentSavingsHeader && (
          <label
            htmlFor="currentSavings"
            className="typography-title-5 font-medium"
          >
            {currentSavingsHeader}
          </label>
        )}
        <div className="relative">
          <input
            id={'currentSavings'}
            data-testid="current-savings"
            type="text"
            inputMode="numeric"
            value={
              currentSavings
                ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0
                  }).format(parseInt(currentSavings))
                : ''
            }
            onFocus={() => setCurrentSavingsTouched(true)}
            onChange={(e) => {
              if (!currentSavingsTouched) setCurrentSavingsTouched(true);
              const value = e.target.value.replace(/[^\d]/g, '');
              const numericValue = parseFloat(value.replace(/,/g, ''));

              if (numericValue <= MAX_SAVINGS_LIMIT || isNaN(numericValue)) {
                handleMonetaryValueChange(value, setCurrentSavings);
              }
            }}
            className={cx(
              'typography-title-4 flex h-10 w-full items-center justify-center gap-2 rounded-input-box-shape  border border-solid border-[#3436361a] px-1 text-center font-medium focus:outline-[#00D9B4]',
              !currentSavingsTouched && 'text-[#676c6f] text-opacity-50'
            )}
          />
        </div>
      </div>
      {/* Regular Contributions */}
      <div className="flex flex-col gap-2">
        {contributionsHeader && (
          <label
            htmlFor="extraContribution"
            className="typography-title-5 font-medium"
          >
            {contributionsHeader}
          </label>
        )}
        <div className="relative">
          <input
            id="extraContribution"
            type="text"
            inputMode="numeric"
            max={10000}
            value={
              extraContribution
                ? new Intl.NumberFormat('en-US', {
                    style: 'currency',
                    currency: 'USD',
                    maximumFractionDigits: 0
                  }).format(parseInt(extraContribution))
                : ''
            }
            onFocus={() => setExtraContributionTouched(true)}
            onChange={(e) => {
              if (!extraContributionTouched) setExtraContributionTouched(true);
              const value = e.target.value.replace(/[^\d]/g, '');
              const numericValue = parseFloat(value.replace(/,/g, ''));

              if (
                numericValue <= MAX_CONTRIBUTIONS_LIMIT ||
                isNaN(numericValue)
              ) {
                handleMonetaryValueChange(value, setExtraContribution);
              }
            }}
            className={cx(
              'typography-title-4 flex h-10 w-full items-center justify-center gap-2 rounded-input-box-shape  border border-solid border-[#3436361a] px-1 text-center font-medium focus:outline-[#00D9B4]',
              !extraContributionTouched && 'text-[#676c6f] text-opacity-50'
            )}
          />
        </div>
      </div>
      {/* Contribution Frequency */}
      <div className="flex flex-col gap-2">
        {frequencyHeader && (
          <label className="typography-title-5 font-medium">
            {frequencyHeader}
          </label>
        )}
        {frequencyOptions && (
          <div className="flex gap-2">
            {frequencyOptions.map((label) => {
              let freq = 12;
              switch (label) {
                case 'Bi-weekly':
                  freq = 26;
                  break;
                case 'Daily':
                  freq = 365;
                  break;
                case 'Monthly':
                  break;
                case 'Quarterly':
                  freq = 4;
                  break;
                case 'Weekly':
                  freq = 52;
                  break;
                case 'Yearly':
                  freq = 1;
                  break;
              }
              return (
                <button
                  key={label}
                  className={cx(
                    'typography-title-4 flex h-10 flex-grow  items-center justify-center gap-2 rounded-button-shape border-[1.5px] bg-white px-1 font-medium hover:border-teal',
                    contributionFrequency === freq
                      ? '!border-secondary-dark'
                      : 'border-[#3436361a]'
                  )}
                  onClick={() => setContributionFrequency(freq)}
                >
                  {label}
                </button>
              );
            })}
          </div>
        )}
      </div>
      {/* Savings Age Range */}
      <div className="flex flex-col gap-3">
        {savingsRangeHeader && (
          <label className="">
            <span className="typography-title-5 font-medium">
              {savingsRangeHeader}:{' '}
            </span>
            <span className="typography-body-2 font-normal">
              {Math.max(0, savingsAgeRange[1] - savingsAgeRange[0])} years
            </span>
          </label>
        )}
        <Range
          values={savingsAgeRange}
          step={1}
          min={1}
          max={18}
          onChange={(values) => setSavingsAgeRange(values as [number, number])}
          renderTrack={({ props, children }) => (
            <div
              {...props}
              className="mb-[14px] mt-[54px] h-1 rounded-lg bg-blue pr-2"
              style={{
                background: getTrackBackground({
                  values: savingsAgeRange,
                  colors: ['#DFF6F0', '#0A7266', '#DFF6F0'],
                  min: 1,
                  max: 18
                }),
                position: 'relative',
                marginInline: 'auto',
                width: 'calc(100% - 16px)'
              }}
            >
              {children}
            </div>
          )}
          renderThumb={({ index, props, isDragged }) => (
            <div {...props} className="relative -mt-[1.15rem] !h-15 !w-16">
              <div
                className={classNames(
                  'absolute bottom-0 left-5 flex h-6 w-6 items-center justify-center rounded-full p-1 shadow-lg transition-colors duration-300',
                  { 'bg-teal-800': !isDragged, 'bg-primary': isDragged }
                )}
              />
              <div className="typography-title-5 absolute bottom-7 left-1/2 flex -translate-x-1/2 transform text-nowrap rounded bg-teal-800 px-2 py-1 text-center font-medium text-white">
                {savingsAgeRange[index]} y/o
              </div>
            </div>
          )}
        />
      </div>
      {/* Savings Rate */}
      <div>
        {savingsRateHeader && (
          <p className="typography-title-5 pb-2 font-medium">
            {savingsRateHeader}
          </p>
        )}
        {savingsRateBody && (
          <p className="typography-body-2 mb-4 font-light">{savingsRateBody}</p>
        )}
        <div className="grid grid-cols-4 gap-2">
          {[
            { percent: 0.06, label: 'Family Shield' },
            { percent: 0.05, label: 'Infinity' },
            { percent: 0.03, label: 'Max' },
            { percent: 0.02, label: 'Core' }
          ].map((option) => {
            function toPercent(percent: number): import('react').ReactNode {
              return (
                <span className="typography-title-3 font-medium">
                  {(percent * 100).toFixed(0)}%
                </span>
              );
            }
            return (
              <button
                key={option.label}
                className={cx(
                  'flex flex-grow flex-col items-center justify-center gap-[2px] rounded-button-shape border-[1.5px] bg-white p-2 hover:border-teal',
                  rateOfReturn === option.percent
                    ? '!border-secondary-dark'
                    : 'border-[#3436361a]'
                )}
                onClick={() => setRateOfReturn(option.percent as any)}
              >
                {toPercent(option.percent)}
                <span className="typography-body-3 font-light">
                  {option.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      {/* Estimated Savings for Mobile */}
      <div className="desktop:hidden">
        <div className="flex flex-col items-center justify-center gap-2 rounded-card-inside-card-shape bg-brand-weakest py-3 desktop:hidden">
          {totalSavingsHeader && (
            <p className="typography-title-5 font-medium">
              {totalSavingsHeader}
            </p>
          )}
          <h2 className="text-[56px] font-normal leading-[64px]">
            {new Intl.NumberFormat('en-US', {
              style: 'currency',
              currency: 'USD',
              minimumFractionDigits: 0,
              maximumFractionDigits: 0
            }).format(formData.estimatedSavings)}
          </h2>
        </div>
      </div>
    </div>
  );
};
